import { Box, Button, Icon } from '@chakra-ui/react'
import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react'
import { VirtuosoGrid, VirtuosoGridHandle } from 'react-virtuoso'
import Selecto from 'react-selecto'
import makeGridComponents from './makeGridComponents'
import useColCountObserver from '../../../../hooks/useColCountObserver'
import Heading from './Heading'
import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import {
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
} from '../../../../interfaces'
import NewFolderElement from './NewFolderElement'
import { FiChevronDown } from 'react-icons/fi'
import { foldersPerPage, filesPerPage } from '../../../../../utils/mediaLibrary'
import NewFileElement from './NewFileElement'
import DragComponent from './Selecto'
import RecentlyCreatedFolderElement from './RecentlyCreatedFolderElement'
import useGridKeyboardNavigation from '../../../../hooks/useKeyboardSelectionv2'

type RecentFileFolder =
  | MediaLibrarySearchFileResponse
  | MediaLibrarySearchFolderResponse
interface GridViewProps {
  scrollEl: HTMLDivElement | null
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  currentMediaCollection: string
  mediaCollectionRoute: boolean
  selectedFiles: MediaLibrarySearchFileResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  fileList: MediaLibrarySearchFileResponse[]
  folderList: MediaLibrarySearchFolderResponse[]
  recentFileFolderList?: RecentFileFolder[]
  folderPage: number
  loadingFolders: boolean
  filePage: number
  loadingFiles: boolean
  folderPath: string
  minHeight: string
}

const GridViewNew = React.memo(
  ({
    scrollEl,
    dispatch,
    fileList,
    folderList,
    recentFileFolderList = [],
    selectedFiles,
    selectedFolders,
    currentMediaCollection,
    mediaCollectionRoute,
    folderPage,
    loadingFolders,
    filePage,
    loadingFiles,
    folderPath,
    minHeight,
  }: GridViewProps) => {
    const [colCount, setColCount] = useState(3)
    const [focusedIndex, setFocusedIndex] = useState<{
      index: number
      type: string
      id: string
    } | null>(null)
    const gridOrder = ['recent', 'folder', 'file']
    const assetCountMap = {
      recent: recentFileFolderList.length,
      folder: folderList.length,
      file: fileList.length,
    }

    const fileRef = useRef<VirtuosoGridHandle>(null)
    const folderRef = useRef<VirtuosoGridHandle>(null)
    const recentRef = useRef<VirtuosoGridHandle>(null)

    const selectoRef = React.useRef<Selecto>(null)
    const keyActiveElementRef = React.useRef<HTMLDivElement | null>(null)

    const selectoDragMarker = React.useRef<{ x: number; y: number } | null>(
      null,
    )

    const assetRefMap = {
      recent: recentRef,
      folder: folderRef,
      file: fileRef,
    }

     function isFile(
      item: RecentFileFolder,
    ): item is MediaLibrarySearchFileResponse {
      return item.type === 'file'
    }

    function isFileVersion(
      item: RecentFileFolder,
    ): item is MediaLibrarySearchFileResponse {
      return item.type === 'file-version'
    }

    function isFolder(
      item: RecentFileFolder,
    ): item is MediaLibrarySearchFolderResponse {
      return item.type === 'folder'
    }

    const getIdMap = useMemo(
      () => ({
        recent: (index: number) => {
          const asset = recentFileFolderList[index]
          if (isFolder(asset)) {
            return asset.folderId
          }
          if (isFile(asset)) {
            return asset.fileId || '';
          }
          if (isFileVersion(asset as MediaLibrarySearchFileResponse)) {
            return (asset as MediaLibrarySearchFileResponse).versionInfo?.id ?? (asset as MediaLibrarySearchFileResponse).fileId
          }
          return ''
        },
        folder: (index: number) => folderList[index]?.folderId ?? '',
        file: (index: number) =>
         isFileVersion(fileList[index] as MediaLibrarySearchFileResponse) ? fileList[index]?.versionInfo?.id ?? fileList[index]?.fileId : fileList[index]?.fileId,
      }),
      [recentFileFolderList, folderList, fileList],
    )

    // preparing this set so that finding in recent items is O(1)
    const recentFileFolderSet = useMemo(() => {
      return new Set(
        recentFileFolderList.map(asset => {
          if (isFolder(asset)) {
            return asset.folderId
          }
          if (isFile(asset)) {
            return asset.fileId
          }
          if (isFileVersion(asset as MediaLibrarySearchFileResponse)) {
            return (asset as MediaLibrarySearchFileResponse).versionInfo?.id ?? (asset as MediaLibrarySearchFileResponse).fileId
          }
        })
      )
    }, [recentFileFolderList])

    // preparing this set so that finding in fileList is O(1)
    const fileListMap = useMemo(() => {
      return new Map(
        fileList.map(file => [ isFileVersion(file as MediaLibrarySearchFileResponse) ? file.versionInfo?.id ?? file.fileId : file.fileId, file]),
      )
    }, [fileList])

    // preparing this map so that finding in folderList is O(1)
    const folderListMap = useMemo(() => {
      return new Map(folderList.map(folder => [folder.folderId, folder]))
    }, [folderList])

    const selectedAssetMap: Record<
      'recent' | 'folder' | 'file',
      Set<string>
    > = useMemo(() => {
      const selectedRecentLocal = new Set<string>()
      const selectedFoldersLocal = new Set<string>()
      const selectedFilesLocal = new Set<string>()
      for (const f of selectedFolders) {
        if (recentFileFolderSet.has(f.folderId)) {
          selectedRecentLocal.add(f.folderId)
        } else {
          selectedFoldersLocal.add(f.folderId)
        }
      }

      for (const f of selectedFiles) {
        if (recentFileFolderSet.has( isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId)) {
          selectedRecentLocal.add(isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId)
        } else {
          selectedFilesLocal.add(isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId)
        }
      }
      return {
        recent: selectedRecentLocal,
        folder: selectedFoldersLocal,
        file: selectedFilesLocal,
      }
    }, [selectedFolders, selectedFiles, recentFileFolderSet])

    const setSelectedAssetMap = useMemo(
      () => ({
        recent: (newSet: Set<string>) => {
          const recentSelectedFolders: MediaLibrarySearchFolderResponse[] =
            recentFileFolderList.filter(
              (i): i is MediaLibrarySearchFolderResponse =>
                isFolder(i) && newSet.has(i.folderId),
            )
          const recentSelectedFiles: MediaLibrarySearchFileResponse[] =
            recentFileFolderList.filter(
              (i): i is MediaLibrarySearchFileResponse =>
                (isFile(i) || isFileVersion(i)) && newSet.has(isFileVersion(i as MediaLibrarySearchFileResponse) ? i.versionInfo?.id ?? i.fileId : i.fileId),
            )

          const existingSelectedFolders: MediaLibrarySearchFolderResponse[] = []
          const existingSelectedFiles: MediaLibrarySearchFileResponse[] = []
          selectedAssetMap['folder'].forEach(id => {
            const folder = folderListMap.get(id)
            if (folder) {
              existingSelectedFolders.push(folder)
            }
          })
          selectedAssetMap['file'].forEach(id => {
            const file = fileListMap.get(id)
            if (file) {
              existingSelectedFiles.push(file)
            }
          })
          dispatch([
            {
              type: MediaLibraryActions.SET_SELECTED_FOLDERS,
              payload: [...recentSelectedFolders, ...existingSelectedFolders],
            },
            {
              type: MediaLibraryActions.SET_SELECTED_FILES,
              payload: [...recentSelectedFiles, ...existingSelectedFiles],
            },
          ])
        },
        folder: (newSet: Set<string>) => {
          const folders = folderList.filter(f => newSet.has(f.folderId))
          const recentFolders: MediaLibrarySearchFolderResponse[] =
            recentFileFolderList.filter(
              (i): i is MediaLibrarySearchFolderResponse =>
                isFolder(i) && selectedAssetMap.recent.has(i.folderId),
            )
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_FOLDERS,
            payload: [...folders, ...recentFolders],
          })
        },
        file: (newSet: Set<string>) => {
          const files = fileList.filter(f =>
            newSet.has(isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId),
          )
          const recentFiles: MediaLibrarySearchFileResponse[] =
            recentFileFolderList.filter(
              (i): i is MediaLibrarySearchFileResponse =>
                (isFile(i) || isFileVersion(i)) &&
                selectedAssetMap.recent.has(isFileVersion(i as MediaLibrarySearchFileResponse) ? i.versionInfo?.id ?? i.fileId : i.fileId),
            )
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_FILES,
            payload: [...files, ...recentFiles],
          })
        },
      }),
      [dispatch, folderList, fileList, recentFileFolderList, selectedAssetMap],
    )

    const handleKeyDown = useGridKeyboardNavigation({
      gridOrder,
      assetCountMap,
      getIdMap,
      colCount,
      selectedAssetMap,
      setSelectedAssetMap,
      focusedIndex,
      setFocusedIndex,
      assetRefMap,
      dispatch,
      route: '/media-library',
    })

    const gridComponentsMap = useMemo(() => ({
      recent: makeGridComponents(colCount, 'recent-grid'),
      folder: makeGridComponents(colCount, 'folder-grid'),
      file: makeGridComponents(colCount, 'file-grid'),
    }), [colCount])

    useColCountObserver(scrollEl, setColCount)

    const handleLoadMoreFiles = React.useCallback(e => {
      e.stopPropagation()
      dispatch({ type: MediaLibraryActions.LOAD_MORE_FILES })
    }, [])

    const handleLoadMoreFolders = React.useCallback(e => {
      e.stopPropagation()
      dispatch({ type: MediaLibraryActions.LOAD_MORE_FOLDERS })
    }, [])

    const refreshTargets = useCallback(
      () =>
        requestAnimationFrame(() =>
          selectoRef.current?.findSelectableTargets(),
        ),
      [selectoRef.current],
    )

     useEffect(() => {
       if (focusedIndex?.id) {
         const { index, type, id } = focusedIndex
         if (
        !selectedAssetMap[type].has(id)
         ) {
           setFocusedIndex(null)
         }
       }
     }, [JSON.stringify(focusedIndex),selectedAssetMap])

     return (
       <Box
         height={'100%'}
         minHeight={minHeight}
         outline={'none'}
         width={'100%'}
         paddingX={'24px'}
         paddingTop={'24px'}
         ref={keyActiveElementRef}
         tabIndex={0}
         position="relative"
         zIndex={0} // ensure grid stays below header (header zIndex is higher)
         onClick={() => {
           // do not stop event propagation here, as we want to close any open context menu, stop it in MediaLibraryLayout
           // event.stopPropagation();

           // drag can happen in two ways
           //1.Complete drag inside the single grid element like inside files and folders, in that case click event is propogated to onCLick
           // listen of element and it does not comes to this outer box onCLick.
           //2. Drag end outside the grid element , or it covers multiple grid elements and in that case
           //  click event is propogated to this outer box onClick. So here we check if drag has occured we return it  else clear selection

           setFocusedIndex(null)
           Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
             selectedAssetMap[type].clear()
             selected(selectedAssetMap[type])
           })

           dispatch({type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS})
         }}
         onKeyDown={handleKeyDown}
       >
         {scrollEl && (
           <>
             <DragComponent
               selectedAssetMap={selectedAssetMap}
               setSelectedAssetMap={setSelectedAssetMap}
               setFocusedIndex={setFocusedIndex}
               selectoRef={selectoRef}
               scrollEl={scrollEl}
               selectoDragMarker={selectoDragMarker}
               assetCountMap={assetCountMap}
               getIdMap={getIdMap}
               colCount={colCount}
               gridOrder={gridOrder}
               keyActiveElementRef={keyActiveElementRef}
             ></DragComponent>

             {recentFileFolderList.length > 0 ? (
               <>
                 <Heading name="Recently created by you" isFirst={true} className='recent-heading'/>
                 <VirtuosoGrid
                   data={recentFileFolderList}
                   ref={recentRef}
                   customScrollParent={scrollEl}
                   components={gridComponentsMap.recent}
                   rangeChanged={refreshTargets}
                   overscan={0}
                   itemContent={(index, item) =>
                     item.type === 'file' ? (
                       <NewFileElement
                         gridTypeForDataset="recent"
                         getIdMap={getIdMap}
                         setFocusedIndex={setFocusedIndex}
                         selectedAssetMap={selectedAssetMap}
                         setSelectedAssetMap={setSelectedAssetMap}
                         focusedIndex={focusedIndex}
                         index={index}
                         dispatch={dispatch}
                         mediaCollectionRoute={mediaCollectionRoute}
                         currentMediaCollection={currentMediaCollection || ''}
                         folderPath={folderPath}
                         file={item as MediaLibrarySearchFileResponse}
                         gridOrder={gridOrder}
                         assetCountMap={assetCountMap}
                       />
                     ) : (
                       <RecentlyCreatedFolderElement
                         gridTypeForDataset="recent"
                         getIdMap={getIdMap}
                         setFocusedIndex={setFocusedIndex}
                         selectedAssetMap={selectedAssetMap}
                         setSelectedAssetMap={setSelectedAssetMap}
                         focusedIndex={focusedIndex}
                         index={index}
                         dispatch={dispatch}
                         mediaCollectionRoute={mediaCollectionRoute}
                         currentMediaCollection={currentMediaCollection || ''}
                         folder={item as MediaLibrarySearchFolderResponse}
                         gridOrder={gridOrder}
                         assetCountMap={assetCountMap}
                       />
                     )
                   }
                 />
               </>
             ) : null}

             {folderList.length > 0 && (
               <>
                 <Heading
                   name="Folders"
                   isFirst={recentFileFolderList.length === 0}
                   className='folder-heading'
                 />
                 <VirtuosoGrid
                   data={folderList}
                   ref={folderRef}
                   customScrollParent={scrollEl}
                   components={gridComponentsMap.folder}
                   rangeChanged={refreshTargets}
                   overscan={0}
                   itemContent={(index, item) => (
                     <NewFolderElement
                       getIdMap={getIdMap}
                       setFocusedIndex={setFocusedIndex}
                       selectedAssetMap={selectedAssetMap}
                       setSelectedAssetMap={setSelectedAssetMap}
                       focusedIndex={focusedIndex}
                       index={index}
                       dispatch={dispatch}
                       mediaCollectionRoute={mediaCollectionRoute}
                       currentMediaCollection={currentMediaCollection || ''}
                       folder={item}
                       gridOrder={gridOrder}
                       assetCountMap={assetCountMap}
                     />
                   )}
                 />
                 {(folderList.length % foldersPerPage === 0 &&
                   folderList.length / foldersPerPage === folderPage + 1) ||
                 loadingFolders ? (
                   <Button
                     as="a"
                     variant="link"
                     cursor="pointer"
                     color="appClearBlue"
                     paddingTop={'16px'}
                     onClick={handleLoadMoreFolders}
                     isLoading={loadingFolders}
                     loadingText={'Loading...'}
                     className='folder-see-more'
                   >
                     {'See More'}
                     <Icon as={FiChevronDown} />
                   </Button>
                 ) : null}
               </>
             )}

             {fileList.length > 0 && (
               <>
                 <Heading
                   name="Files"
                   isFirst={
                     recentFileFolderList.length === 0 &&
                     folderList.length === 0
                   }
                  className='file-heading'
                 />
                 <VirtuosoGrid
                   data={fileList}
                   customScrollParent={scrollEl}
                   components={gridComponentsMap.file}
                   overscan={0}
                   ref={fileRef}
                   rangeChanged={refreshTargets}
                   itemContent={(index, item) => (
                     <NewFileElement
                       getIdMap={getIdMap}
                       setFocusedIndex={setFocusedIndex}
                       selectedAssetMap={selectedAssetMap}
                       setSelectedAssetMap={setSelectedAssetMap}
                       focusedIndex={focusedIndex}
                       index={index}
                       dispatch={dispatch}
                       mediaCollectionRoute={mediaCollectionRoute}
                       currentMediaCollection={currentMediaCollection || ''}
                       folderPath={folderPath}
                       file={item}
                       gridOrder={gridOrder}
                       assetCountMap={assetCountMap}
                     />
                   )}
                 />
                 {(fileList.length % filesPerPage === 0 &&
                   fileList.length / filesPerPage === filePage + 1) ||
                 loadingFiles ? (
                   <Button
                     as="a"
                     variant="link"
                     cursor="pointer"
                     color="appClearBlue"
                     paddingTop={'16px'}
                     onClick={handleLoadMoreFiles}
                     isLoading={loadingFiles}
                     loadingText={'Loading...'}
                     className='file-see-more'
                   >
                     {'See More'}
                     <Icon as={FiChevronDown} />
                   </Button>
                 ) : null}
               </>
             )}
           </>
         )}

         <Box className='padding-bottom' height={'24px'}></Box>
       </Box>
     )
  },
)

export default GridViewNew
